/**
 * @file pages/Contact.js
 * @description Page de contact avec formulaire validé
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Contact = () => {
  const { sendContact, user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!form.name || form.name.length < 2) newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Adresse email invalide';
    if (!form.message || form.message.length < 10) newErrors.message = 'Le message doit contenir au moins 10 caractères';
    return newErrors;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await sendContact(form.name, form.email, form.message);
      setSuccess('Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
      setForm({ name: user?.name || '', email: '', message: '' });
    } catch (err) {
      setApiError(err.response?.data?.error || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .contact-page {
          min-height: 100vh;
          padding: 100px 24px 80px;
          background: linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
          position: relative;
        }
        .contact-page::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 40% at 50% 0%, rgba(169, 134, 103, 0.1), transparent 60%);
          pointer-events: none;
        }
        .contact-container {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1.6fr;
          gap: 64px;
          align-items: start;
          position: relative;
        }
        /* Left side */
        .contact-info {
          animation: fadeInUp 0.5s ease;
        }
        .contact-label {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #A98667;
          margin-bottom: 16px;
        }
        .contact-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          color: #FFFFFF;
          line-height: 1.2;
          margin-bottom: 20px;
        }
        .contact-desc {
          color: rgba(240, 234, 226, 0.6);
          line-height: 1.8;
          font-size: 1rem;
          margin-bottom: 48px;
        }
        .contact-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .contact-card {
          background: rgba(31, 42, 68, 0.4);
          border: 1px solid rgba(169, 134, 103, 0.12);
          border-radius: 14px;
          padding: 22px;
          display: flex;
          align-items: flex-start;
          gap: 16px;
          transition: border-color 0.2s;
        }
        .contact-card:hover { border-color: rgba(169, 134, 103, 0.25); }
        .contact-card-icon {
          width: 44px;
          height: 44px;
          background: rgba(169, 134, 103, 0.12);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          flex-shrink: 0;
        }
        .contact-card-title {
          font-weight: 600;
          color: #FFFFFF;
          font-size: 0.9rem;
          margin-bottom: 4px;
        }
        .contact-card-text {
          color: rgba(240, 234, 226, 0.55);
          font-size: 0.875rem;
          line-height: 1.5;
        }
        /* Form */
        .contact-form-container {
          background: rgba(31, 42, 68, 0.5);
          border: 1px solid rgba(169, 134, 103, 0.15);
          border-radius: 20px;
          padding: 40px;
          animation: fadeInUp 0.5s ease 0.1s both;
        }
        .contact-form-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.6rem;
          color: #FFFFFF;
          margin-bottom: 28px;
        }
        .form-group { margin-bottom: 20px; }
        .form-label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgba(240, 234, 226, 0.65);
          margin-bottom: 8px;
        }
        .form-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 10px;
          padding: 14px 16px;
          color: #FFFFFF;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          outline: none;
        }
        .form-input:focus {
          border-color: #A98667;
          background: rgba(169, 134, 103, 0.06);
          box-shadow: 0 0 0 3px rgba(169, 134, 103, 0.1);
        }
        .form-input.error { border-color: #e74c3c; }
        .form-input::placeholder { color: rgba(240, 234, 226, 0.25); }
        textarea.form-input { resize: vertical; min-height: 140px; line-height: 1.6; }
        .field-error {
          color: #e74c3c;
          font-size: 0.8rem;
          margin-top: 6px;
        }
        .form-char {
          text-align: right;
          font-size: 0.78rem;
          color: rgba(240, 234, 226, 0.35);
          margin-top: 4px;
        }
        .contact-submit {
          width: 100%;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          color: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
        }
        .contact-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(169, 134, 103, 0.4);
        }
        .contact-submit:disabled { opacity: 0.7; cursor: not-allowed; }
        .feedback-msg {
          padding: 14px 18px;
          border-radius: 10px;
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 20px;
          animation: fadeInUp 0.3s ease;
        }
        .feedback-msg.success {
          background: rgba(39, 174, 96, 0.1);
          border: 1px solid rgba(39, 174, 96, 0.25);
          color: #27ae60;
        }
        .feedback-msg.error {
          background: rgba(231, 76, 60, 0.1);
          border: 1px solid rgba(231, 76, 60, 0.25);
          color: #e74c3c;
        }
        @media (max-width: 900px) {
          .contact-container { grid-template-columns: 1fr; gap: 40px; }
        }
        @media (max-width: 500px) {
          .contact-form-container { padding: 28px 20px; }
        }
      `}</style>

      <div className="contact-page">
        <div className="contact-container">
          {/* Infos */}
          <div className="contact-info">
            <p className="contact-label">✦ Nous contacter</p>
            <h1 className="contact-title">Une question ?<br />Écrivez-nous</h1>
            <p className="contact-desc">
              Nous sommes là pour vous aider dans votre parcours d'étude biblique. 
              Toute question, suggestion ou demande est la bienvenue.
            </p>

            <div className="contact-cards">
              {[
                { icon: '✉️', title: 'Email', text: 'codessystemdigital@gmail.com' },
                { icon: '📖', title: 'Ressources', text: 'Consultez nos moments d\'étude pour commencer votre parcours biblique.' },
                { icon: '🙏', title: 'Communauté', text: 'Engageons-nous dans la Parole.' },
              ].map((card, i) => (
                <div className="contact-card" key={i}>
                  <div className="contact-card-icon">{card.icon}</div>
                  <div>
                    <p className="contact-card-title">{card.title}</p>
                    <p className="contact-card-text">{card.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="contact-form-container">
            <h2 className="contact-form-title">Envoyer un message</h2>

            {success && <div className="feedback-msg success">✓ {success}</div>}
            {apiError && <div className="feedback-msg error">⚠ {apiError}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="name">Votre nom</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Jean Dupont"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && <div className="field-error">⚠ {errors.name}</div>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="email">Adresse email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="votre@email.com"
                  value={form.email}
                  onChange={handleChange}
                />
                {errors.email && <div className="field-error">⚠ {errors.email}</div>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="message">Votre message</label>
                <textarea
                  id="message"
                  name="message"
                  className={`form-input ${errors.message ? 'error' : ''}`}
                  placeholder="Décrivez votre question, suggestion ou demande..."
                  value={form.message}
                  onChange={handleChange}
                  maxLength={1000}
                />
                <p className="form-char">{form.message.length}/1000</p>
                {errors.message && <div className="field-error">⚠ {errors.message}</div>}
              </div>

              <button type="submit" className="contact-submit" disabled={loading}>
                {loading ? 'Envoi en cours...' : '✦ Envoyer le message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
